import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, CalendarDays, Clock, Info } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { ApiError } from "../api/client";
import {
  fetchAvailability,
  fetchServices,
  fetchTenant,
  type AvailabilitySlot,
} from "../api/catalog";
import { createBooking } from "../api/bookings";
import { Button, Card, ErrorState, PageHeader, Spinner } from "../components/ui";
import { formatDateLong, formatIDR } from "../lib/format";
import type { ServicePublic } from "../api/types";

function toDateInput(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const JS_DAY_NAMES = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];

const minDate = (() => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return toDateInput(d);
})();

/** Tujuh tanggal terdekat (mulai besok) sebagai pilihan cepat. */
const dayChips = (() => {
  const base = new Date();
  base.setHours(0, 0, 0, 0);
  const chips: Array<{ iso: string; day: number }> = [];
  for (let i = 1; i <= 7; i++) {
    const d = new Date(base);
    d.setDate(base.getDate() + i);
    chips.push({ iso: toDateInput(d), day: d.getDay() });
  }
  return chips;
})();

export function PublicTenantPage() {
  const { slug = "" } = useParams();
  const navigate = useNavigate();
  const { isAuthed, user } = useAuth();

  const tenantQuery = useQuery({
    queryKey: ["tenant", slug],
    queryFn: () => fetchTenant(slug),
    enabled: !!slug,
  });
  const servicesQuery = useQuery({
    queryKey: ["tenant-services", slug],
    queryFn: () => fetchServices(slug),
    enabled: !!slug,
  });

  const [serviceId, setServiceId] = useState<string | null>(null);
  const [date, setDate] = useState<string>("");
  const [slot, setSlot] = useState<AvailabilitySlot | null>(null);
  const [notes, setNotes] = useState("");
  const [payOnArrival, setPayOnArrival] = useState(false); // false = bayar online QRIS
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Gender pasien (bila sudah login) dipakai server untuk hanya menghitung terapis sejenis.
  const patientGender = user?.gender ?? null;
  const availabilityQuery = useQuery({
    queryKey: ["availability", slug, serviceId, date, patientGender],
    queryFn: () =>
      fetchAvailability(slug, { serviceId: serviceId!, date, gender: patientGender }),
    enabled: !!slug && !!serviceId && !!date,
  });

  const service = servicesQuery.data?.find((s) => s.id === serviceId);
  // 3 langkah: 1) layanan 2) jadwal 3) ringkasan & bayar (terapis dipilih sistem)
  const step = !serviceId ? 1 : !date || !slot ? 2 : 3;
  const slots = availabilityQuery.data?.slots ?? [];

  function onDateChange(value: string): void {
    setSlot(null);
    setDate(value);
  }

  function resetFromStep(): void {
    setError(null);
    if (step === 1) {
      setDate("");
      setSlot(null);
    }
    if (step <= 2) {
      setSlot(null);
    }
  }

  async function submitBooking() {
    if (!service || !slot || !serviceId) return;
    setError(null);
    if (!isAuthed) {
      navigate("/login", { state: { from: `/t/${slug}` } });
      return;
    }
    setSubmitting(true);
    try {
      const result = await createBooking({
        tenantSlug: slug,
        serviceId,
        date,
        startTime: slot.startTime,
        notes: notes || undefined,
        payOnArrival,
      });
      if (payOnArrival || result.booking.status === "CONFIRMED") {
        navigate(`/pasien/appointments/${result.booking.id}`, { replace: true });
      } else {
        navigate(`/pasien/pay/${result.booking.id}`, { replace: true });
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Gagal membuat booking. Coba lagi.");
    } finally {
      setSubmitting(false);
    }
  }

  if (tenantQuery.isPending || servicesQuery.isPending) {
    return <Spinner label="Memuat lokasi…" />;
  }
  if (tenantQuery.isError || servicesQuery.isError || !tenantQuery.data) {
    return (
      <div className="container">
        <ErrorState onRetry={() => void tenantQuery.refetch()} />
      </div>
    );
  }
  const tenant = tenantQuery.data;

  return (
    <div className="auth-shell">
      <div className="container">
        <Link to={isAuthed ? "/pasien/booking" : "/"} className="back-link">
          <ArrowLeft size={16} /> Pilih lokasi lain
        </Link>
        <Card>
          <div className="tenant-card-head">
            <span className="tenant-logo" aria-hidden>
              {tenant.name.charAt(0)}
            </span>
            <div>
              <h2 style={{ margin: 0 }}>{tenant.name}</h2>
              <p className="muted" style={{ margin: "2px 0 0" }}>
                {tenant.address}
              </p>
            </div>
          </div>
          <p className="tenant-hours">
            Buka {tenant.openingTime}–{tenant.closingTime}
          </p>
        </Card>

        <div className="stepper" aria-hidden>
          {[1, 2, 3].map((s) => (
            <span
              key={s}
              className={`step-dot ${s < step ? "done" : s === step ? "current" : ""}`}
            />
          ))}
        </div>

        <PageHeader title={`Langkah ${step} dari 3`} />

        <p className="info-note">
          <Info size={15} aria-hidden />
          <span>
            <strong>Terapis dipilihkan sistem secara adil.</strong> Anda tidak memilih terapis —
            sistem membagi jadwal merata di antara terapis berdinas yang{" "}
            <strong>berjenis kelamin sama dengan Anda</strong>. Nama terapis muncul setelah booking
            dibuat.
            {patientGender
              ? null
              : " Lengkapi jenis kelamin di halaman Profil agar terapis yang bertugas sesuai."}
          </span>
        </p>

        {/* Langkah 1: layanan */}
        {step === 1 ? (
          <div className="choice-list">
            {(servicesQuery.data ?? []).map((s: ServicePublic) => (
              <button
                key={s.id}
                type="button"
                className={`choice-item ${s.id === serviceId ? "selected" : ""}`}
                onClick={() => setServiceId(s.id)}
              >
                <span className="choice-main">
                  <span className="choice-title">{s.name}</span>
                  <span className="choice-sub">
                    {s.durationMinutes} menit
                    {s.description ? ` · ${s.description}` : ""}
                  </span>
                </span>
                <span className="choice-end">{formatIDR(s.price)}</span>
              </button>
            ))}
          </div>
        ) : null}

        {/* Langkah 2: tanggal & jam */}
        {step === 2 ? (
          <div>
            <label className="label">Tanggal</label>
            <div className="day-chip-row" role="group" aria-label="Pilih tanggal cepat">
              {dayChips.map((c) => (
                <button
                  key={c.iso}
                  type="button"
                  className={`day-chip ${date === c.iso ? "selected" : ""}`}
                  onClick={() => onDateChange(c.iso)}
                >
                  {JS_DAY_NAMES[c.day]?.slice(0, 3)}, {c.iso.slice(8)}
                </button>
              ))}
            </div>
            <input
              type="date"
              className="input date-field"
              min={minDate}
              value={date}
              onChange={(e) => onDateChange(e.target.value)}
            />
            {date ? (
              availabilityQuery.isPending ? (
                <Spinner label="Mencari jam tersedia…" />
              ) : availabilityQuery.isError ? (
                <ErrorState
                  title="Gagal memuat jam tersedia."
                  onRetry={() => void availabilityQuery.refetch()}
                />
              ) : slots.length === 0 ? (
                <div className="state-box">
                  <p className="state-title">Tidak ada jam tersedia pada {formatDateLong(date)}.</p>
                  <p className="state-hint">Coba pilih tanggal lain.</p>
                </div>
              ) : (
                <>
                  <label className="label">Jam</label>
                  <div className="slot-grid">
                    {slots.map((s) => {
                      const selected = slot?.startTime === s.startTime;
                      return (
                        <button
                          key={s.startTime}
                          type="button"
                          className={`slot-btn ${selected ? "selected" : ""}`}
                          onClick={() => setSlot(s)}
                        >
                          <span className="t">
                            {s.startTime}–{s.endTime}
                          </span>
                          <span className="n">{s.therapistCount} terapis siap</span>
                        </button>
                      );
                    })}
                  </div>
                </>
              )
            ) : null}
          </div>
        ) : null}

        {/* Langkah 3: ringkasan & bayar */}
        {step === 3 && service && slot ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Card className="summary-list">
              <div className="summary-row">
                <span>Layanan</span>
                <strong>{service.name}</strong>
              </div>
              <div className="summary-row">
                <span>Terapis</span>
                <strong>Ditentukan sistem (sesuai jenis kelamin Anda)</strong>
              </div>
              <div className="summary-row">
                <span>Tanggal</span>
                <strong>{formatDateLong(date)}</strong>
              </div>
              <div className="summary-row">
                <span>Jam</span>
                <strong>
                  {slot.startTime}–{slot.endTime}
                </strong>
              </div>
              <div className="summary-row">
                <span>Total</span>
                <strong>{formatIDR(service.price)}</strong>
              </div>
            </Card>
            <label className="label">Metode pembayaran</label>
            <div className="choice-list" role="radiogroup" aria-label="Metode pembayaran">
              <button
                type="button"
                role="radio"
                aria-checked={!payOnArrival}
                className={`choice-item ${!payOnArrival ? "selected" : ""}`}
                onClick={() => setPayOnArrival(false)}
              >
                <span className="choice-main">
                  <span className="choice-title">Bayar online (QRIS)</span>
                  <span className="choice-sub">
                    Lunasi sebelum batas waktu agar booking tidak kadaluarsa.
                  </span>
                </span>
                <span className="choice-end">QRIS</span>
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={payOnArrival}
                className={`choice-item ${payOnArrival ? "selected" : ""}`}
                onClick={() => setPayOnArrival(true)}
              >
                <span className="choice-main">
                  <span className="choice-title">Bayar di tempat</span>
                  <span className="choice-sub">
                    Booking langsung terkunci — bayar tunai saat tiba di lokasi.
                  </span>
                </span>
                <span className="choice-end">Tunai</span>
              </button>
            </div>
            {payOnArrival ? (
              <p className="state-hint" style={{ margin: 0 }}>
                ✅ Tidak perlu bayar online — booking tidak akan kadaluarsa.
              </p>
            ) : null}
            <label className="label" htmlFor="notes">
              Catatan (opsional)
            </label>
            <textarea
              id="notes"
              className="input"
              rows={2}
              value={notes}
              placeholder="Mis. keluhan pegal punggung bawah…"
              onChange={(e) => setNotes(e.target.value)}
            />
            {error ? <p className="alert alert-error">{error}</p> : null}
            <Button
              onClick={() => void submitBooking()}
              loading={submitting}
              className="btn-block btn-lg"
            >
              {payOnArrival
                ? "Buat Booking — Bayar di Tempat"
                : "Buat Booking & Lanjut ke Pembayaran"}
            </Button>
          </div>
        ) : null}

        {/* Navigasi antar langkah */}
        {step > 1 ? (
          <Button
            variant="ghost"
            onClick={() => {
              if (step === 2) {
                setServiceId(null);
              }
              resetFromStep();
            }}
          >
            <ArrowLeft size={16} /> Kembali
          </Button>
        ) : null}

        {step === 2 && date ? (
          <p className="state-hint" style={{ marginTop: 8 }}>
            <Clock size={14} /> {slots.length} jam tersedia — jadwal diperbarui otomatis dari
            lokasi.
          </p>
        ) : null}

        {step === 2 && !date ? (
          <p className="state-hint">
            <CalendarDays size={14} /> Pilih tanggal untuk melihat jam yang tersedia.
          </p>
        ) : null}
      </div>
    </div>
  );
}
